import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import StarField from '../effects/StarField';

export default function Benefits() {
  const { t } = useTranslation();

  const problems = [
    { icon: 'scatter_plot', text: t('benefits.problems.fragmented') },
    { icon: 'help_outline', text: t('benefits.problems.noExpert') },
    { icon: 'edit_note', text: t('benefits.problems.manual') },
    { icon: 'visibility_off', text: t('benefits.problems.noVisibility') },
  ];

  const differentials = [
    { icon: 'support_agent', title: t('benefits.differentials.concierge.title'), desc: t('benefits.differentials.concierge.desc') },
    { icon: 'extension', title: t('benefits.differentials.extension.title'), desc: t('benefits.differentials.extension.desc') },
    { icon: 'code', title: t('benefits.differentials.saas.title'), desc: t('benefits.differentials.saas.desc') },
    { icon: 'psychology', title: t('benefits.differentials.ai.title'), desc: t('benefits.differentials.ai.desc') },
  ];

  const solutions = [
    { icon: 'hub', text: t('benefits.solutions.centralized') },
    { icon: 'support_agent', text: t('benefits.solutions.conciergeAI') },
    { icon: 'bolt', text: t('benefits.solutions.automation') },
    { icon: 'monitoring', text: t('benefits.solutions.visibility') },
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-transparent">
      <StarField starCount={180} speed={0.2} />
      <motion.img src="/logo-icon.png" alt="" aria-hidden="true" className="absolute -top-24 -left-24 w-[400px] h-[400px] object-contain opacity-[0.03] rotate-[20deg] pointer-events-none select-none" animate={{ scale: [1, 1.06, 1], opacity: [0.03, 0.055, 0.03] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />

      <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-20px" }} transition={{ duration: 0.7 }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight text-white">
            {t('benefits.problemsTitle')} <span className="gradient-text">{t('benefits.problemsTitleHighlight')}</span>
          </h2>
          <p className="text-on-surface-variant text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            {t('benefits.problemsSubtitle')}
          </p>
        </motion.div>

        {/* Before -> After transformation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mb-20 rounded-2xl overflow-hidden border border-outline-variant/15">
          {/* BEFORE */}
          <motion.div
            className="p-6 md:p-8 bg-red-950/5 relative overflow-hidden border-b md:border-b-0 md:border-r border-red-900/15"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="material-symbols-outlined text-red-400/70 text-[22px]">close</span>
              <span className="text-sm font-bold font-headline text-red-400/80 uppercase tracking-wider">{t('benefits.without')}</span>
            </div>
            <div className="space-y-4">
              {problems.map((p, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                >
                  <span className="material-symbols-outlined text-red-400/40 text-[20px] mt-0.5">{p.icon}</span>
                  <p className="text-sm text-red-300/60 leading-relaxed">{p.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* AFTER */}
          <motion.div
            className="p-6 md:p-8 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(168,67,151,0.08), rgba(210,60,179,0.03))' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="material-symbols-outlined text-green-400 text-[22px]">check_circle</span>
              <span className="text-sm font-bold font-headline text-green-400 uppercase tracking-wider">{t('benefits.with')}</span>
            </div>
            <div className="space-y-4">
              {solutions.map((s, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                >
                  <div className="w-7 h-7 rounded-md bg-purple-primary/15 border border-purple-primary/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="material-symbols-outlined text-purple-primary text-[16px]">{s.icon}</span>
                  </div>
                  <p className="text-sm text-on-surface/90 leading-relaxed">{s.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-20px" }} transition={{ duration: 0.7 }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight text-white">
            {t('benefits.differentialsTitle')} <span className="gradient-text">{t('benefits.differentialsTitleHighlight')}</span>
          </h2>
          <p className="text-on-surface-variant text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            {t('benefits.differentialsSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentials.map((d, i) => (
            <motion.div key={i} className="text-center p-5 sm:p-8 bg-[#15121a] border border-outline-variant/30 rounded-xl" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.6 }} whileHover={{ y: -6, borderColor: "rgba(168,67,151,0.5)", boxShadow: "0 12px 40px rgba(168,67,151,0.1)" }}>
              <motion.div className="w-14 h-14 rounded-lg bg-purple-primary/10 border border-purple-primary/30 flex items-center justify-center mx-auto mb-5" whileHover={{ scale: 1.15, rotate: -5 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}>
                <span className="material-symbols-outlined text-purple-primary text-[28px]">{d.icon}</span>
              </motion.div>
              <h3 className="text-lg font-bold font-headline mb-2 text-white">{d.title}</h3>
              <p className="text-sm text-on-surface-variant leading-relaxed">{d.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div className="text-center mt-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <motion.a href="https://compliance.crosspaysolutions.app/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline shadow-lg shadow-purple-primary/20 relative overflow-hidden group" whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(168,67,151,0.4)" }} whileTap={{ scale: 0.97 }}>
            <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative">{t('benefits.ctaPlatform')}</span>
            <motion.span className="material-symbols-outlined text-[18px] relative" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>arrow_forward</motion.span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
